import type { Coding, Condition } from '@medplum/fhirtypes'
import { orderBy, uniq } from 'lodash-es'
import {
  ICD9_SYSTEM,
  ICD10_SYSTEM,
  SNOMED_CT_SYSTEM,
} from '#src/codeSystems.ts'
import type { ExportFields } from '#src/ExtendedResource.ts'
import {
  CONDITION_CATEGORIES,
  ConditionCategory,
  ConditionClinicalStatus,
  ConditionVerificationStatus,
} from '#src/models/conditions/constants.ts'
import {
  ExtendedCondition,
  type ExtendedConditionParams,
  type ExtendedConditionSearchQueryOptions,
} from '#src/models/conditions/ExtendedCondition.ts'
import { icd10CodesDisplayFamilySortKey } from '#src/models/conditions/icd10DisplayFamily.ts'
import type { QueryTypes } from '#src/search.ts'

// A diagnosis's codes grouped by coding system, so matching can compare
// like-for-like rather than pooling every code into one flat set. ICD-9 is
// carried for completeness even though matchDiagnosisByCodes does not treat it
// as a match signal (see that method for why).
export interface DiagnosisCodeSet {
  icd9: string[]
  icd10: string[]
  snomed: string[]
}

export const FACESHEET_CONDITION_SORT_MODES = [
  'anatomical',
  'chronological',
  'alphabetical',
] as const

export type FacesheetConditionSortMode =
  (typeof FACESHEET_CONDITION_SORT_MODES)[number]

export const FACESHEET_CONDITION_SORT_DIRECTIONS = ['asc', 'desc'] as const

export type FacesheetConditionSortDirection =
  (typeof FACESHEET_CONDITION_SORT_DIRECTIONS)[number]

export class Diagnosis extends ExtendedCondition {
  static isInstance(
    resource: ExtendedCondition | Condition
  ): resource is Diagnosis {
    const ec =
      resource instanceof ExtendedCondition
        ? resource
        : ExtendedCondition.fromFhirResource(resource)

    return ec.category === ConditionCategory.DIAGNOSIS
  }

  static searchQuery(options: ExtendedConditionSearchQueryOptions): QueryTypes {
    return {
      ...ExtendedCondition.searchQuery(options),
      category: CONDITION_CATEGORIES[ConditionCategory.DIAGNOSIS],
    }
  }

  static queryByIds(ids: string[]): QueryTypes {
    return {
      'verification-status:not': ConditionVerificationStatus.ENTERED_IN_ERROR,
      _id: ids,
      _count: ids.length,
      _sort: '-_lastUpdated',
    }
  }

  static sortForDisplay<TDiagnosis extends Diagnosis>(
    diagnoses: readonly TDiagnosis[]
  ): TDiagnosis[] {
    return orderBy(
      diagnoses,
      [
        (diagnosis) => diagnosis.icd10DisplayFamilySortKey,
        (diagnosis) => diagnosis.elationRank ?? Number.MAX_SAFE_INTEGER,
        (diagnosis) =>
          diagnosis.clinicalStatus === ConditionClinicalStatus.CONTROLLED,
        (diagnosis) => diagnosis.onsetDate,
        (diagnosis) => diagnosis.title,
      ],
      ['asc', 'asc', 'asc', 'desc', 'asc']
    )
  }

  static sortForFacesheet<TDiagnosis extends Diagnosis>(
    diagnoses: readonly TDiagnosis[],
    mode: FacesheetConditionSortMode,
    direction?: FacesheetConditionSortDirection
  ): TDiagnosis[] {
    switch (mode) {
      case 'anatomical':
        // Anatomical is the body-system order of sortForDisplay, which callers
        // have already applied, so the input order is kept.
        return [...diagnoses]
      case 'chronological': {
        const chronologicalDirection = direction ?? 'desc'
        const missingDate =
          chronologicalDirection === 'desc' ? '' : '9999-12-31'
        return orderBy(
          diagnoses,
          [
            (diagnosis) => diagnosis.onsetDateString ?? missingDate,
            (diagnosis) => diagnosis.name.toLowerCase(),
          ],
          [chronologicalDirection, 'asc']
        )
      }
      case 'alphabetical':
        return orderBy(
          diagnoses,
          [(diagnosis) => diagnosis.name.toLowerCase()],
          [direction ?? 'asc']
        )
    }
  }

  constructor({
    title,
    note,
    ...rest
  }: ExtendedConditionParams & {
    title?: string
    note?: string
  }) {
    super({
      category: ConditionCategory.DIAGNOSIS,
      ...rest,
    })
    if (title) {
      this.title = title
    }
    if (note) {
      this.note = note
    }
  }

  private get codings(): Coding[] {
    return this.resource.code?.coding ?? []
  }

  get icd9Codes(): string[] {
    return this.codings
      .filter((code) => code.system === ICD9_SYSTEM)
      .map((code) => code.code ?? 'UNKNOWN')
  }

  set icd9Codes(codes: string[]) {
    this.resource.code ??= {}
    this.resource.code.coding = [
      ...this.codings.filter((code) => code.system !== ICD9_SYSTEM),
      ...uniq(codes).map((code) => ({
        system: ICD9_SYSTEM,
        code,
      })),
    ]
  }

  get icd10Codes(): string[] {
    return this.codings
      .filter((coding) => coding.system === ICD10_SYSTEM)
      .map((coding) => coding.code ?? 'UNKNOWN')
  }

  set icd10Codes(codes: string[]) {
    this.resource.code ??= {}
    this.resource.code.coding = [
      ...this.codings.filter((code) => code.system !== ICD10_SYSTEM),
      ...uniq(codes).map((code) => ({
        system: ICD10_SYSTEM,
        code,
      })),
    ]
  }

  get icd10DisplayFamilySortKey(): number {
    return icd10CodesDisplayFamilySortKey(this.icd10Codes)
  }

  get snomedCodes(): string[] {
    return this.codings
      .filter((code) => code.system === SNOMED_CT_SYSTEM)
      .map((code) => code.code ?? 'UNKNOWN')
  }

  set snomedCodes(codes: { code: string; display?: string }[]) {
    this.resource.code ??= {}
    this.resource.code.coding = [
      ...this.codings.filter((code) => code.system !== SNOMED_CT_SYSTEM),
      ...uniq(codes).map((code) => ({
        system: SNOMED_CT_SYSTEM,
        code: code.code,
        display: code.display,
      })),
    ]
  }

  get allCodes(): string[] {
    return uniq([...this.icd9Codes, ...this.icd10Codes, ...this.snomedCodes])
  }

  get codeSet(): DiagnosisCodeSet {
    return {
      icd9: this.icd9Codes,
      icd10: this.icd10Codes,
      snomed: this.snomedCodes,
    }
  }

  get name(): string {
    return this.code?.text ?? this.title
  }

  // Two codings describe the same clinical problem only when they share a
  // SPECIFIC diagnosis code — an ICD-10 or SNOMED CT code both sides carry.
  // ICD-9 is intentionally excluded from the signal: Elation's ICD-9 crosswalk
  // is coarse and collapses many distinct ICD-10 problems onto the same generic
  // supplementary code (e.g. V49.89), so an ICD-9 overlap is not evidence of the
  // same problem. Comparing per system, rather than scoring one intersection
  // ratio against the existing condition's code count, is what makes this
  // correct in both directions: a sparsely-coded condition (e.g. an
  // Orbit-native ICD-10-only entry) still matches a triple-coded Elation problem
  // that shares that ICD-10, while a condition that shares only a generic ICD-9
  // code — its ICD-10 identity differing or absent — no longer over-matches a
  // different problem.
  static matchDiagnosisByCodes(
    diagnoses: Diagnosis[],
    incoming: DiagnosisCodeSet
  ): Diagnosis[] {
    const incomingIcd10 = new Set(incoming.icd10.filter(Boolean))
    const incomingSnomed = new Set(incoming.snomed.filter(Boolean))
    return diagnoses.filter((diagnosis) => {
      const sharesIcd10 = diagnosis.icd10Codes.some((code) =>
        incomingIcd10.has(code)
      )
      const sharesSnomed = diagnosis.snomedCodes.some((code) =>
        incomingSnomed.has(code)
      )
      return sharesIcd10 || sharesSnomed
    })
  }

  protected getExportFields(): ExportFields {
    return {
      icd9Codes: this.icd9Codes,
      icd10Codes: this.icd10Codes,
      snomedCodes: this.snomedCodes,
    }
  }
}
